import React, { useEffect, useMemo, useState } from "react";
import ReactGridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { Plus } from "lucide-react";

import BackgroundStage from "../backgrounds/BackgroundStage";
import useDashboardLayout from "./useDashboardLayout";
import { DashboardWidgetContext } from "./WidgetContext";
import { WIDGET_REGISTRY, repackForCols, widgetsForRole } from "./widgets/registry";
import { shouldPlayIntro, disarmIntro } from "../../motion/introMotion";
import WidgetCatalog from "./WidgetCatalog";

const { Responsive, WidthProvider } = ReactGridLayout;
const ResponsiveGrid = WidthProvider(Responsive);

// True tablet tier: desktop 12, tablet-wide 12, tablet 8, phone 4, narrow 1.
const COLS = { lg: 12, md: 12, sm: 8, xs: 4, xxs: 1 };
const BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };

// Widgets that should span full width at small tiers to stay usable.
const WIDE_IDS = [
  "date-time-attendance",
  "daily-briefing",
  "reports-analytics",
];

const DashboardGrid = ({ kind }) => {
  const {
    loaded,
    layout,
    hidden,
    prefs,
    setLayout,
    setWidgetPrefs,
    toggleHidden,
    reset,
    editMode,
    setEditMode,
    saving,
    savedAt,
    visibleWidgets,
  } = useDashboardLayout(kind);

  // Edit-mode personalisation surface: the widget catalogue drawer.
  const [catalogOpen, setCatalogOpen] = useState(false);

  const roleWidgets = useMemo(() => widgetsForRole(kind), [kind]);

  // Leaving edit mode dismisses the catalogue.
  useEffect(() => {
    if (!editMode) setCatalogOpen(false);
  }, [editMode]);

  const widgetCtx = useMemo(
    () => ({ editMode, prefs, setWidgetPrefs }),
    [editMode, prefs, setWidgetPrefs]
  );

  // Post-login intro: widgets sweep in as a diagonal wave (top-left →
  // bottom-right), staggered by grid position. Plays once, then disarms so
  // every later dashboard visit renders instantly.
  const [intro, setIntro] = useState(() => shouldPlayIntro());
  useEffect(() => {
    if (!intro || !loaded) return undefined;
    const t = setTimeout(() => {
      setIntro(false);
      disarmIntro();
    }, 2600);
    return () => clearTimeout(t);
  }, [intro, loaded]);

  // Per-widget wave delay from its 12-col grid position.
  const waveDelay = (item) =>
    `${Math.min(0.55 + (item.x * 0.25 + item.y) * 0.09, 1.5)}s`;

  // Only render grid items whose widget is visible (not hidden + allowed for
  // role), then re-apply registry constraints (min size, resize lock, pinned
  // size) so they hold even over a stale persisted layout.
  const renderableLayout = useMemo(() => {
    const visibleIds = new Set(visibleWidgets.map((w) => w.id));
    return layout
      .filter((l) => visibleIds.has(l.i))
      .map((l) => {
        const w = WIDGET_REGISTRY[l.i];
        if (!w) return l;
        const next = { ...l };
        if (w.minW != null) next.minW = w.minW;
        if (w.minH != null) next.minH = w.minH;
        if (w.maxW != null) next.maxW = w.maxW;
        if (w.maxH != null) next.maxH = w.maxH;
        // Resizing is opt-in: only widgets flagged `resizable` get a handle.
        if (!w.resizable) next.isResizable = false;
        if (w.fixedSize) {
          next.w = w.fixedSize.w;
          next.h = w.fixedSize.h;
          next.minW = w.fixedSize.w;
          next.maxW = w.fixedSize.w;
          next.minH = w.fixedSize.h;
          next.maxH = w.fixedSize.h;
        }
        return next;
      });
  }, [layout, visibleWidgets]);

  const layouts = useMemo(() => {
    return {
      lg: renderableLayout,
      md: renderableLayout,
      sm: repackForCols(renderableLayout, 8, { wideIds: WIDE_IDS }),
      xs: repackForCols(renderableLayout, 4, {
        wideIds: WIDE_IDS,
        defaultW: 4,
      }),
      xxs: repackForCols(renderableLayout, 1, { defaultW: 1 }),
    };
  }, [renderableLayout]);

  const savedLabel = saving
    ? "Saving…"
    : savedAt
      ? `Saved ${savedAt.toLocaleTimeString()}`
      : "";

  return (
    <DashboardWidgetContext.Provider value={widgetCtx}>
      <BackgroundStage />
      <div
        className={`dsh-wrap dash-grid-wrap dsh-grid-${kind} ${
          editMode ? "is-edit" : ""
        }${intro ? " dash-intro" : ""}`}
      >
        <div className="dash-grid-toolbar">
          <span className="dash-grid-saved">{savedLabel}</span>
          {editMode ? (
            <>
              <button
                type="button"
                className="dash-personalise-btn dash-addwidget-btn"
                onClick={() => setCatalogOpen(true)}
                title="Browse and add widgets"
              >
                <Plus size={15} />
                <span className="dash-btn-label">Add widget</span>
              </button>
              <button
                type="button"
                className="dash-reset-btn"
                onClick={reset}
                title="Reset to default layout"
              >
                Reset
              </button>
              <button
                type="button"
                className="dash-done-btn"
                onClick={() => setEditMode(false)}
              >
                Done
              </button>
            </>
          ) : (
            <button
              type="button"
              className="dash-personalise-btn dash-personalise-fab"
              onClick={() => setEditMode(true)}
              title="Rearrange and resize widgets"
              aria-label="Personalise dashboard"
            >
              <span className="dash-personalise-label">✎ Personalise</span>
              <span className="dash-personalise-fab-icon" aria-hidden="true">
                ✎
              </span>
            </button>
          )}
        </div>

        {loaded && (
          <ResponsiveGrid
            className="dash-rgl"
            layouts={layouts}
            cols={COLS}
            breakpoints={BREAKPOINTS}
            rowHeight={80}
            margin={[18, 18]}
            containerPadding={[0, 0]}
            isDraggable={editMode}
            isResizable={editMode}
            draggableHandle=".dash-drag-handle"
            onLayoutChange={(next, allLayouts) => {
              // Only persist user-initiated edits of the 12-col layout.
              if (!editMode) return;
              setLayout(next);
            }}
          >
            {renderableLayout.map((item) => {
              const w = WIDGET_REGISTRY[item.i];
              if (!w) return null;
              const { Component } = w;
              return (
                <div
                  key={item.i}
                  className={`dash-widget ${w.className || ""}`}
                  data-widget={w.id}
                  style={intro ? { "--dash-delay": waveDelay(item) } : undefined}
                >
                  {editMode && (
                    <>
                      <button
                        type="button"
                        className="dash-drag-handle"
                        aria-label="Drag to move"
                        title="Drag to move"
                      >
                        <span className="dash-grip"></span>
                      </button>
                      <button
                        type="button"
                        className="dash-hide-btn"
                        aria-label="Hide widget"
                        title="Hide widget"
                        onClick={() => toggleHidden(w.id)}
                      >
                        ×
                      </button>
                    </>
                  )}
                  <div className="dash-widget-body">
                    <Component />
                  </div>
                </div>
              );
            })}
          </ResponsiveGrid>
        )}

        {/* Widget catalogue — slide-in drawer, edit mode only. */}
        {catalogOpen && (
          <WidgetCatalog
            widgets={roleWidgets}
            hidden={hidden}
            onAdd={(id) => hidden.includes(id) && toggleHidden(id)}
            onRemove={(id) => !hidden.includes(id) && toggleHidden(id)}
            onClose={() => setCatalogOpen(false)}
          />
        )}
      </div>
    </DashboardWidgetContext.Provider>
  );
};

export default DashboardGrid;
